import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { adminApi } from "../../api/admin";
import { Alert, Badge, Card, Spinner } from "../../ui";
import { stateTone } from "./shared";

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
}

/** Every product side by side, the owner's view of the platform (docs/14 §1, §3). */
export function PlatformOverviewPage() {
  const metrics = useQuery({
    queryKey: ["admin", "metrics", "platform"],
    queryFn: () => adminApi.platformMetrics(),
  });

  if (metrics.isLoading) return <Spinner />;
  if (metrics.isError) {
    return (
      <Alert kind="error">Only an owner can see the platform overview.</Alert>
    );
  }
  const rows = metrics.data?.items ?? [];

  return (
    <Card>
      <h1 className="mb-1 text-xl font-semibold">Products</h1>
      <p className="mb-3 text-sm text-slate-500">
        Last {metrics.data?.days ?? 30} days.
      </p>
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase text-slate-500">
          <tr>
            <th className="px-3 py-1">Product</th>
            <th className="px-3 py-1">Users</th>
            <th className="px-3 py-1">Active</th>
            <th className="px-3 py-1">Revenue</th>
            <th className="px-3 py-1">Failed</th>
            <th className="px-3 py-1">Disputes</th>
            <th className="px-3 py-1">Dead webhooks</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.product} className="border-t border-slate-100">
              <td className="px-3 py-2">
                <Link
                  className="font-medium text-brand hover:underline"
                  to={`/admin/p/${row.product}`}
                >
                  {row.name}
                </Link>{" "}
                <Badge tone={stateTone(row.status)}>{row.status}</Badge>
                <div className="text-xs text-slate-500">
                  {row.capabilities.join(", ") || "no sections"}
                </div>
              </td>
              <td className="px-3 py-2">{row.users}</td>
              <td className="px-3 py-2">{row.active_users}</td>
              <td className="px-3 py-2">{money(row.revenue)}</td>
              <td className="px-3 py-2">{row.failed_payments}</td>
              <td className="px-3 py-2">{row.open_disputes}</td>
              <td className="px-3 py-2">{row.dead_outbox}</td>
            </tr>
          ))}
          {rows.length === 0 ? (
            <tr>
              <td className="px-3 py-3 text-slate-500" colSpan={7}>
                No products yet.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </Card>
  );
}
